import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "./BrandMark";
import { useQuizStore } from "../state/store";

/**
 * Static explainer at /help — "Wie funktioniert das?" — reachable from the
 * Settings sheet. Lays out the five status categories (Gesamt/Neu/Läuft/
 * Falsch/Fertig), the three learning rules, and two example flows. Public
 * route so it can be linked from welcome mail / login funnel later (issue
 * #66, mockup at docs/Design-Kits/how-it-works-mockup.html).
 */
export function HelpPage(): JSX.Element {
  // Live binding: reflects whatever the admin has added/deleted via
  // /admin/vocab. Falls back to a sensible literal if the quiz store
  // hasn't finished booting (rare — the user has to actively navigate
  // here, which only happens post-boot).
  const totalActive = useQuizStore((s) => s.vocab.length);
  const totalLabel = totalActive > 0 ? `${totalActive}` : "alle";

  return (
    <main className="min-h-full">
      <div className="mx-auto w-full max-w-2xl px-4 pt-6 pb-10 sm:px-6 sm:pt-10 space-y-6 safe-top">
        <header className="flex items-center justify-between gap-3">
          <Link
            to="/"
            aria-label="PPLearning — Startseite"
            className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong rounded-lg"
          >
            <BrandMark variant="horizontal" className="h-7 sm:h-8 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="help-back-home"
            className="text-sm text-accent hover:underline"
          >
            ← Zurück
          </Link>
        </header>

        <section className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-fg">Wie funktioniert das?</h1>
          <p className="text-sm text-fg-muted leading-relaxed">
            PPLearning lernt mit dir mit. Jede Vokabel hat einen <strong className="text-fg">Status</strong>, der dir zeigt, wo du gerade stehst.
          </p>
        </section>

        <section className="space-y-2" data-testid="help-statuses">
          <SectionLabel>Die fünf Stati</SectionLabel>
          <StatusCard
            icon="∑"
            tone="total"
            label="Gesamt"
            hint={`Alle ${totalLabel} Vokabeln aus dem PPL-Kurs.`}
            testid="status-total"
          />
          <StatusCard
            icon="•"
            tone="fresh"
            label="Neu"
            hint="Noch nie abgefragt."
            testid="status-fresh"
          />
          <StatusCard
            icon="↻"
            tone="progress"
            label="Läuft"
            hint="1× richtig — fehlt noch 1× für „Fertig“."
            testid="status-progress"
          />
          <StatusCard
            icon="✕"
            tone="wrong"
            label="Falsch"
            hint="Einmal falsch beantwortet — braucht 3× richtig."
            testid="status-wrong"
          />
          <StatusCard
            icon="✓"
            tone="mastered"
            label="Fertig"
            hint="Sitzt. Kommt seltener dran."
            testid="status-mastered"
          />
        </section>

        <section className="space-y-2" data-testid="help-directions">
          <SectionLabel>Lernrichtung</SectionLabel>
          <DirectionCard
            tone="en"
            label="Englisch → Deutsch"
            hint="Du liest das englische Wort und wählst die deutsche Bedeutung."
            testid="direction-en-de"
          />
          <DirectionCard
            tone="de"
            label="Deutsch → Englisch"
            hint="Du liest den deutschen Begriff und wählst das englische Fachwort."
            testid="direction-de-en"
          />
          <DirectionCard
            tone="mixed"
            label="Gemischt"
            hint="Beides durcheinander — die kleine Markierung oben auf der Karte zeigt dir bei jeder Frage, in welche Richtung es geht."
            testid="direction-mixed"
          />
          <p
            className="bg-accent-soft border-l-[3px] border-accent rounded-r-md px-3 py-2.5 text-sm text-fg-soft leading-relaxed"
            data-testid="direction-tip"
          >
            <strong className="text-fg">Wechseln</strong> kannst du jederzeit im Menü. Dein Fortschritt in der anderen Richtung bleibt unberührt.
          </p>
        </section>

        <section className="space-y-2" data-testid="help-rules">
          <SectionLabel>Die Regeln</SectionLabel>
          <RuleCard num={1} title="Eine neue Vokabel braucht 2× richtig." testid="rule-1">
            Bei der ersten richtigen Antwort wechselt sie auf <strong className="text-fg">Läuft</strong>. Bei der zweiten richtigen ist sie <strong className="text-fg">Fertig</strong>.
          </RuleCard>
          <RuleCard num={2} title="Eine falsche Antwort kostet 3 zusätzliche richtige." testid="rule-2">
            Egal ob bei <strong className="text-fg">Neu</strong> oder <strong className="text-fg">Läuft</strong> — sobald du falsch antwortest, springt sie auf <strong className="text-fg">Falsch</strong> und braucht <strong className="text-fg">3× richtig</strong>, bevor sie als <strong className="text-fg">Fertig</strong> gilt.
          </RuleCard>
          <RuleCard num={3} title="Falsche und Neue kommen häufiger dran." testid="rule-3">
            Spaced Repetition — der Trainer wählt die nächste Frage so aus, dass schwere Wörter öfter auftauchen und sichere seltener wiederholt werden.
          </RuleCard>
          <p
            className="text-xs text-fg-muted italic leading-relaxed pl-1 pt-1"
            data-testid="rules-direction-note"
          >
            Diese Regeln gelten in jeder Richtung unabhängig — eine Vokabel kann z. B. in EN→DE bereits <strong className="text-fg not-italic">Fertig</strong> sein und in DE→EN noch <strong className="text-fg not-italic">Neu</strong>.
          </p>
        </section>

        <section className="space-y-2" data-testid="help-certificates">
          <SectionLabel>Drei Zertifikate</SectionLabel>
          <DirectionCard
            tone="cert-en"
            label="EN→DE Zertifikat"
            hint="Alle Vokabeln in der Richtung Englisch → Deutsch fertig."
            testid="cert-en-de"
          />
          <DirectionCard
            tone="cert-de"
            label="DE→EN Zertifikat"
            hint="Alle Vokabeln in der Richtung Deutsch → Englisch fertig."
            testid="cert-de-en"
          />
          <DirectionCard
            tone="cert-master"
            label="Master-Zertifikat"
            hint="Beide Richtungen vollständig fertig — die höchste Auszeichnung."
            testid="cert-master"
          />
        </section>

        <section className="space-y-2" data-testid="help-markers">
          <SectionLabel>Vokabeln markieren</SectionLabel>
          <DirectionCard
            tone="mark"
            label="Stern im Quiz"
            hint="Oben rechts auf der Quiz-Karte zeigt ein Stern, ob die aktuelle Vokabel markiert ist. Antippen markiert sie für intensives Nachüben."
            testid="mark-star"
          />
          <DirectionCard
            tone="mark"
            label="Modus: Nur Markierte"
            hint="Im Menü unter Modus kannst du auf 'Nur Markierte' umschalten — dann ziehen alle Fragen nur noch aus deiner Markiert-Liste. Wechsle jederzeit zurück auf 'Alle Vokabeln'."
            testid="mark-mode"
          />
          <DirectionCard
            tone="mark"
            label="Liste: Meine Vokabeln"
            hint="Im Menü → 'Meine Vokabeln' findest du alle Wörter mit Filtern (Alle / Markierte / Falsch beantwortete) plus einer Suche. Pro Zeile kannst du markieren oder entmarkieren."
            testid="mark-list"
          />
          <p
            className="bg-warning-soft border-l-[3px] border-warning rounded-r-md px-3 py-2.5 text-sm text-fg-soft leading-relaxed"
            data-testid="mark-tip"
          >
            <strong className="text-fg">Wichtig:</strong> Antworten im Markiert-Modus zählen <strong className="text-fg">nicht</strong> für den Zertifikats-Fortschritt. Es ist eine reine Übungs-Umgebung — dein Hauptkurs-Stand bleibt unberührt, egal wie oft du im Markiert-Modus richtig oder falsch antwortest.
          </p>
        </section>

        <section className="space-y-2" data-testid="help-flows">
          <SectionLabel>So sieht ein typischer Verlauf aus</SectionLabel>

          <div
            className="bg-card border border-outline-soft rounded-xl p-4 space-y-2"
            data-testid="flow-smooth"
          >
            <p className="text-sm font-semibold text-fg">Glatter Durchlauf</p>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Pill tone="fresh">Neu</Pill>
              <Step tone="correct">✓</Step>
              <Arrow />
              <Pill tone="progress">Läuft</Pill>
              <Step tone="correct">✓</Step>
              <Arrow />
              <Pill tone="mastered">Fertig</Pill>
            </div>
          </div>

          <div
            className="bg-card border border-outline-soft rounded-xl p-4 space-y-2"
            data-testid="flow-stumble"
          >
            <p className="text-sm font-semibold text-fg">Mit einem Stolperer</p>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Pill tone="fresh">Neu</Pill>
              <Step tone="wrong">✕</Step>
              <Arrow />
              <Pill tone="wrong">Falsch</Pill>
              <Step tone="correct">✓✓✓</Step>
              <Arrow />
              <Pill tone="mastered">Fertig</Pill>
            </div>
          </div>
        </section>

        <p
          className="bg-accent-soft border-l-[3px] border-accent rounded-r-md px-3 py-2.5 text-sm text-fg-soft leading-relaxed"
          data-testid="help-tip"
        >
          <strong className="text-fg">Tipp:</strong> Du kannst deinen Fortschritt jederzeit über das Menü zurücksetzen — oder eine einzelne Vokabel später ganz normal nochmal richtig beantworten.
        </p>

        <p
          className="text-xs text-fg-faint text-center pt-2"
          data-testid="help-disclaimer-hint"
        >
          Übersetzungen und KI-Beispiele ohne Gewähr —{" "}
          <Link to="/haftung" className="text-accent hover:underline">
            siehe Haftungsausschluss
          </Link>
          .
        </p>
      </div>
    </main>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-xs uppercase tracking-widest text-fg-faint">{children}</p>;
}

type StatusTone = "total" | "fresh" | "progress" | "wrong" | "mastered";

const TONE_ICON_CLASS: Record<StatusTone, string> = {
  total: "bg-white/5 text-fg-muted",
  fresh: "bg-accent/15 text-accent",
  progress: "bg-warning-soft text-warning",
  wrong: "bg-danger-soft text-danger",
  mastered: "bg-success-soft text-success",
};

function StatusCard({
  icon,
  tone,
  label,
  hint,
  testid,
}: {
  icon: string;
  tone: StatusTone;
  label: string;
  hint: string;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className="bg-card border border-outline-soft rounded-xl p-3 sm:p-3.5 grid grid-cols-[auto_1fr] gap-3 items-center"
    >
      <span
        aria-hidden
        className={`w-10 h-10 rounded-lg inline-flex items-center justify-center text-lg font-bold ${TONE_ICON_CLASS[tone]}`}
      >
        {icon}
      </span>
      <div className="space-y-0.5">
        <div className="text-sm font-semibold text-fg">{label}</div>
        <div className="text-xs text-fg-muted leading-snug">{hint}</div>
      </div>
    </div>
  );
}

type DirectionTone = "en" | "de" | "mixed" | "cert-en" | "cert-de" | "cert-master" | "mark";

const DIRECTION_TONE_CLASS: Record<DirectionTone, string> = {
  en: "bg-accent-soft text-accent",
  de: "bg-warning-soft text-warning",
  mixed: "bg-[rgb(168_85_247_/_0.15)] text-[rgb(168_85_247)]",
  "cert-en": "bg-accent-soft text-accent",
  "cert-de": "bg-warning-soft text-warning",
  "cert-master": "bg-[rgb(168_85_247_/_0.15)] text-[rgb(168_85_247)]",
  mark: "bg-warning-soft text-warning",
};

const DIRECTION_TONE_ICON: Record<DirectionTone, string> = {
  en: "→",
  de: "←",
  mixed: "⇄",
  "cert-en": "✓",
  "cert-de": "✓",
  "cert-master": "★",
  mark: "★",
};

function DirectionCard({
  tone,
  label,
  hint,
  testid,
}: {
  tone: DirectionTone;
  label: string;
  hint: string;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className="bg-card border border-outline-soft rounded-xl p-3 sm:p-3.5 grid grid-cols-[auto_1fr] gap-3 items-center"
    >
      <span
        aria-hidden
        className={`w-10 h-10 rounded-lg inline-flex items-center justify-center text-lg font-bold ${DIRECTION_TONE_CLASS[tone]}`}
      >
        {DIRECTION_TONE_ICON[tone]}
      </span>
      <div className="space-y-0.5">
        <div className="text-sm font-semibold text-fg">{label}</div>
        <div className="text-xs text-fg-muted leading-snug">{hint}</div>
      </div>
    </div>
  );
}

function RuleCard({
  num,
  title,
  children,
  testid,
}: {
  num: number;
  title: string;
  children: ReactNode;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className="bg-card border border-outline-soft rounded-xl p-3 sm:p-3.5 space-y-1.5"
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className="w-6 h-6 rounded-full bg-accent text-accent-on text-xs font-bold inline-flex items-center justify-center shrink-0"
        >
          {num}
        </span>
        <h3 className="text-sm font-semibold text-fg leading-snug">{title}</h3>
      </div>
      <p className="text-xs text-fg-soft leading-relaxed pl-[34px]">{children}</p>
    </div>
  );
}

type PillTone = "fresh" | "progress" | "wrong" | "mastered";

const PILL_CLASS: Record<PillTone, string> = {
  fresh: "bg-accent/15 text-accent",
  progress: "bg-warning-soft text-warning",
  wrong: "bg-danger-soft text-danger",
  mastered: "bg-success-soft text-success",
};

const DOT_CLASS: Record<PillTone, string> = {
  fresh: "bg-accent",
  progress: "bg-warning",
  wrong: "bg-danger",
  mastered: "bg-success",
};

function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return (
    <span
      data-testid={`pill-${tone}`}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-semibold ${PILL_CLASS[tone]}`}
    >
      <span aria-hidden className={`w-1.5 h-1.5 rounded-full ${DOT_CLASS[tone]}`} />
      {children}
    </span>
  );
}

function Step({ tone, children }: { tone: "correct" | "wrong"; children: ReactNode }) {
  return (
    <span
      className={`font-mono px-1.5 py-0.5 rounded text-[11px] ${tone === "correct" ? "text-success bg-success-soft" : "text-danger bg-danger-soft"}`}
    >
      {children}
    </span>
  );
}

function Arrow() {
  return (
    <span aria-hidden className="text-fg-faint">
      →
    </span>
  );
}
